import { useEffect, useState } from 'react';
import { DataTable, EmptyState, Icon, Stat, Tag, type Column } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import type { ResidenceCountRow, ResidencySummaryResult } from '@dayon/contracts';

import { api } from '../../../lib/api.ts';

/**
 * WHO IS ALREADY HERE — the count that makes the policy above it actionable.
 *
 * A residency control with no population behind it asks an operator to switch a
 * licence rule on and discover afterwards whom it would have excluded. This is
 * the answer beforehand, brand-scoped from the session:
 * `GET /api/residency/summary`.
 *
 * TWO THINGS IT REFUSES TO DO.
 *
 *   · IT NEVER RENDERS "never asked" AS A COUNTRY. Players who registered
 *     before `player.player.residence_country` existed carry NULL, which is the
 *     absence of the question rather than an answer to it. It is counted in its
 *     own tile, with its own words, and is deliberately not a row in the table
 *     — folded in it would routinely be the largest "market" on the screen.
 *   · IT NEVER CLAIMS A VERDICT IT CANNOT COMPUTE. The Permitted column is the
 *     real `player.residency_permitted`, and that function takes a BRAND: the
 *     policy is per-brand, so across several brands one country can be admitted
 *     by one and refused by another. With no brand selected the column says
 *     which brand to pick rather than printing a verdict averaged over rules
 *     that disagree.
 */

const columns = (brandSelected: boolean, total: number): Column<ResidenceCountRow>[] => [
  {
    key: 'country',
    header: 'Country',
    render: (r) => (
      <>
        {/* The name comes from the runtime's own region data — the same source
            the sign-up picker and the validator use. A code this runtime cannot
            name prints as the bare code rather than as a blank. */}
        {r.name ?? <span className="text-subtle">unnamed region</span>}{' '}
        <span className="text-subtle tabular">{r.code}</span>
      </>
    ),
  },
  {
    key: 'players',
    header: 'Players',
    numeric: true,
    render: (r) => <span className="tabular">{formatCount(r.players)}</span>,
  },
  {
    key: 'share',
    header: 'Share',
    numeric: true,
    secondary: true,
    render: (r) => (
      <span className="text-muted tabular">
        {total === 0 ? '—' : `${((r.players / total) * 100).toFixed(1)}%`}
      </span>
    ),
  },
  {
    key: 'permitted',
    header: 'Policy today',
    render: (r) =>
      !brandSelected ? (
        <span className="text-subtle">choose one brand</span>
      ) : r.permitted === null ? (
        <span className="text-subtle">not evaluated</span>
      ) : r.permitted ? (
        <Tag tone="neutral">Admitted</Tag>
      ) : (
        <Tag tone="outline">Would be refused</Tag>
      ),
  },
];

export function ResidenceCounts({
  brandId,
  brandLabel,
  reloadKey,
}: {
  /** The header's brand scope, or null for every brand this admin may see. */
  brandId: string | null;
  brandLabel: string;
  /** Bumped by the parent after a policy write, so the verdicts re-read. */
  reloadKey: number;
}) {
  const [summary, setSummary] = useState<ResidencySummaryResult | null>(null);
  const [loading, setLoading] = useState(true);
  /* "Not read" and "no players" are different facts and this panel is a count:
     an operator who reads a zero here concludes nobody has declared a country. */
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = brandId ? `?brandId=${encodeURIComponent(brandId)}` : '';
    api
      .get<ResidencySummaryResult>(`/api/residency/summary${params}`)
      .then((s) => {
        if (cancelled) return;
        setSummary(s);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        /* Cleared deliberately: a previous brand's breakdown left standing
           under a failure notice would be read as this brand's. */
        setSummary(null);
        setFailed(true);
        /* No error modal here. This panel sits beside the policy on a screen
           that already raises one for each of its own reads, and three modals
           stacked over one visit is how an operator learns to dismiss them
           unread. The panel states the failure in place, permanently. */
        void error;
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [brandId, reloadKey]);

  if (loading && !summary) {
    return (
      <div className="skeleton" style={{ height: '180px' }}>
        &nbsp;
      </div>
    );
  }

  if (failed || !summary) {
    return (
      <EmptyState icon="warning-circle" title="The residence breakdown could not be read">
        {/* THIS SENTENCE USED TO OPEN WITH THE ROUTE IT WAITED ON. The path is
            recorded in the scope note at the top of this file, which is where
            anyone debugging a wrong empty state will look; an operator cannot
            call a route, and the address told them nothing this sentence does
            not tell them better. What they must not get wrong is WHICH of the
            two states they are in, and that line is drawn as hard here as it
            was before: this panel is a count, so a zero read as "nobody has
            declared a country" when the truth is "nobody could ask" is what
            sends somebody off to switch a licence rule on the wrong figure. */}
        The request for the breakdown did not come back, so nothing is counted here — not because no
        player has declared a country. Try again shortly.
      </EmptyState>
    );
  }

  const refused = summary.countries.filter((c) => c.permitted === false);
  const refusedPlayers = refused.reduce((sum, c) => sum + c.players, 0);

  return (
    <>
      <div className="stat-grid">
        <Stat
          label="Players in scope"
          value={formatCount(summary.total)}
          delta={`${brandLabel} · ${formatCount(summary.countries.length)} ${
            summary.countries.length === 1 ? 'country' : 'countries'
          } declared`}
        />
        <Stat
          label="Never asked"
          value={formatCount(summary.neverAsked)}
          delta={
            summary.neverAsked === 0
              ? 'every account in scope declared a country'
              : 'registered before the question existed — not a country, and not a refusal'
          }
        />
        <Stat
          label="Would be refused today"
          value={summary.brandId === null ? '—' : formatCount(refusedPlayers)}
          delta={
            summary.brandId === null
              ? 'the policy is per brand — choose one brand in the header to evaluate it'
              : refusedPlayers === 0
                ? 'no account in scope is in a country this brand refuses'
                : `${formatCount(refused.length)} ${refused.length === 1 ? 'country' : 'countries'} — existing accounts are NOT closed by a policy change`
          }
          deltaUp={summary.brandId !== null && refusedPlayers > 0}
        />
      </div>

      <DataTable
        caption="Players by declared country of residence, with what the policy in force would do about each"
        columns={columns(summary.brandId !== null, summary.total)}
        rows={summary.countries}
        rowKey={(r) => r.code}
        empty={
          <EmptyState icon="globe-hemisphere-east" title="No player has declared a country">
            Every account in this scope predates{' '}
            the residence country and was never asked
            where its holder lives. That is a real empty read, not a filter — the question is asked
            at registration, so the count grows with new sign-ups and never retrospectively.
          </EmptyState>
        }
      />

      {summary.neverAsked > 0 ? (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            <strong>{formatCount(summary.neverAsked)}</strong> of{' '}
            <strong>{formatCount(summary.total)}</strong> accounts in scope were never asked where
            their holder lives — they registered before the question existed. They are not counted
            in any country above and no policy here evaluates them: the rule is asked at
            registration and nothing revisits an account afterwards. The roster filters on{' '}
            <span className="tabular">Never asked</span> if you need the list.
          </div>
        </div>
      ) : null}
    </>
  );
}
